import { pluralize } from '@/shared/lib/plural';

/**
 * Все строки интерфейса.
 *
 * Текст в JSX напрямую не пишется — только отсюда. Язык один (русский), но
 * когда появится второй, перевод — это второй такой файл и переключатель, а не
 * обход всех компонентов. Ключи группируются по экранам и по общим понятиям.
 */
export const ru = {
  app: {
    name: 'Flow Do',
  },
  common: {
    loading: 'Загрузка…',
    drag: 'Перетащить',
    retry: 'Повторить',
    reload: 'Перезагрузить страницу',
    save: 'Сохранить',
    cancel: 'Отмена',
    saved: 'Сохранено',
    soon: 'Скоро',
    networkError: 'Не удалось связаться с сервером. Попробуйте ещё раз.',
    unknownError: 'Что-то пошло не так. Попробуйте ещё раз.',
  },
  palette: {
    title: 'Поиск и действия',
    placeholder: 'Найти задачу, страницу, человека… или действие',
    hint: 'Слова, ключ задачи (TSK-12), имя',
    empty: 'Ничего не нашлось',
    searching: 'Ищем…',
    groups: {
      actions: 'Действия',
      tasks: 'Задачи',
      pages: 'Страницы',
      comments: 'Комментарии',
      people: 'Люди',
      spaces: 'Проекты',
    },
    actions: {
      createTask: (title: string) => `Создать задачу «${title}»`,
      createTaskHint: 'В первую колонку этой доски',
      home: 'На главную',
      members: 'Участники',
      inbox: 'Входящие',
      profile: 'Профиль',
      themeDark: 'Тема: тёмная',
      themeLight: 'Тема: светлая',
      themeSystem: 'Тема: как в системе',
    },
    pageOfWorkspace: 'Страница организации',
    commentBy: (name: string) => `${name} в`,
    keys: { open: 'Открыть', navigate: 'Выбрать', close: 'Закрыть' },
  },
  login: {
    title: 'Вход',
    subtitle: 'Рабочее пространство команды. Войдите, чтобы продолжить.',
    email: 'Адрес почты',
    password: 'Пароль',
    submit: 'Войти',
    alreadyIn: 'Вы уже вошли как',
    goHome: 'Перейти в приложение',
    invalidEmail: 'Похоже, это не адрес почты',
    passwordTooShort: 'Пароль не короче 8 символов',
    noAccount: 'Нет учётной записи?',
    register: 'Зарегистрироваться',
    forgot: 'Забыли пароль?',
  },
  forgot: {
    title: 'Сброс пароля',
    subtitle:
      'Укажите адрес, на который зарегистрирована учётная запись, — пришлём ссылку для нового пароля.',
    submit: 'Прислать ссылку',
    sentTitle: 'Проверьте почту',
    sentText: (email: string) =>
      `Если адрес ${email} у нас есть, письмо со ссылкой уже в пути. Ссылка действует час. Не пришло — посмотрите в спаме.`,
    back: 'Вернуться ко входу',
  },
  reset: {
    title: 'Новый пароль',
    subtitle: 'Придумайте новый пароль. После смены все устройства выйдут из системы.',
    password: 'Новый пароль',
    again: 'Ещё раз',
    submit: 'Сменить пароль',
    done: 'Пароль изменён — войдите с новым',
    expired: 'Ссылка устарела или уже использована',
    request: 'Запросить новую',
  },
  register: {
    title: 'Регистрация',
    subtitle:
      'Заведите учётную запись — и попросите владельца пространства пригласить вас по этой почте.',
    name: 'Имя',
    namePlaceholder: 'Как вас показывать в команде',
    nameRequired: 'Укажите имя',
    submit: 'Создать учётную запись',
    haveAccount: 'Уже есть учётная запись?',
    login: 'Войти',
  },
  invites: {
    title: 'Приглашения',
    from: (name: string) => `от ${name}`,
    asRole: (role: string) => `как ${role.toLowerCase()}`,
    accept: 'Принять',
    decline: 'Отклонить',
    accepted: 'Вы в пространстве',
    declined: 'Приглашение отклонено',
    registered: (name: string) =>
      `${name} уже зарегистрирован(а) — увидит приглашение при входе. Ссылка тоже работает.`,
    notRegistered:
      'Этого адреса у нас ещё нет — отправьте человеку ссылку, по ней он зарегистрируется.',
  },
  nav: {
    path: 'Путь',
    home: 'Главная',
    myTasks: 'Мои задачи',
    inbox: 'Входящие',
    members: 'Участники',
    spaces: 'Проекты',
    noSpaces: 'Проектов пока нет',
    newSpace: 'Новый проект',
    allWorkspaces: 'Все пространства',
    search: 'Поиск',
    searchHint: 'Найти задачу, страницу, человека…',
    notifications: 'Уведомления',
    openMenu: 'Открыть меню',
    collapse: 'Свернуть панель',
    expand: 'Развернуть панель',
    closeMenu: 'Закрыть меню',
    sections: 'Разделы',
  },
  userMenu: {
    profile: 'Профиль',
    theme: 'Тема',
    themeSystem: 'Как в системе',
    themeDark: 'Тёмная',
    themeLight: 'Светлая',
    signOut: 'Выйти',
  },
  home: {
    greeting: 'Привет',
    title: 'Ваши пространства',
    empty: 'Пространств пока нет',
    emptyText: 'Вас пригласят — и пространство появится здесь.',
    emptyTextAdmin: 'Создайте первое пространство для команды.',
    create: 'Создать пространство',
    open: 'Открыть',
  },
  workspace: {
    createTitle: 'Новое пространство',
    createText: 'Организация или команда. Внутри будут проекты, доски и задачи.',
    name: 'Название',
    namePlaceholder: 'Моя команда',
    slug: 'Адрес',
    slugHint: 'Строчные латинские буквы, цифры и дефис. Изменить потом нельзя.',
    slugTaken: 'Этот адрес уже занят',
    slugInvalid: 'Только строчные латинские буквы, цифры и дефис',
    created: 'Пространство создано',
    unavailable: 'Пространство недоступно',
    unavailableText: 'Его нет, оно удалено или вас из него исключили.',
    toList: 'К списку пространств',
    homeSubtitle: 'Проекты, в которые вы допущены.',
    homeTitle: 'Проектов пока нет',
    homeText: 'Вас пока не добавили ни в один проект — попросите владельца или администратора.',
    homeTextAdmin: 'Создайте первый: отдел или проект, внутри — доски и задачи.',
    members: 'Участники',
    switch: 'Сменить пространство',
    role: {
      owner: 'Владелец',
      admin: 'Администратор',
      member: 'Участник',
      guest: 'Гость',
    },
    roleHint: {
      owner: 'Всё, включая передачу владения',
      admin: 'Видит все проекты, управляет участниками',
      member: 'Только свои проекты, может создавать новые',
      guest: 'Только свои проекты, создавать не может',
    },
  },
  space: {
    createTitle: 'Новый проект',
    createText:
      'Отдел или проект: «Маркетинг», «Сайт клиента». Внутри — доски и задачи. Кто его увидит — решаете вы, добавляя людей.',
    name: 'Название',
    namePlaceholder: 'Маркетинг',
    slug: 'Адрес',
    slugHint: 'Изменить потом нельзя.',
    description: 'Описание',
    descriptionPlaceholder: 'Чем занимается проект — коротко',
    color: 'Цвет',
    create: 'Создать проект',
    created: 'Проект создан',
    saved: 'Проект сохранён',
    slugTaken: 'Этот адрес уже занят',
    tabs: {
      boards: 'Доски',
      sprints: 'Спринты',
      pages: 'Страницы',
      members: 'Участники',
      settings: 'Настройки',
    },
    membersTitle: 'Участники проекта',
    membersText:
      'Владельцы и администраторы пространства видят проект всегда. Публичный проект видят все участники пространства — здесь только те, кому выдана отдельная роль.',
    membersTextPrivate:
      'Приватный проект видят только люди из этого списка — плюс владельцы и администраторы пространства.',
    addMember: 'Добавить',
    addMemberTitle: 'Добавить в проект',
    pickPerson: 'Кто',
    pickPersonPlaceholder: 'Имя или адрес почты…',
    nobodyFound: 'Никого не нашлось',
    noMembersYet: 'Пока никого не добавили — проект видят только владельцы и администраторы',
    implicitAccess: 'Все проекты',
    implicitHint: 'Доступ по роли в пространстве — из проекта не убрать',
    memberAdded: 'Участник добавлен',
    memberRemoved: 'Участник убран',
    settingsTitle: 'Настройки проекта',
    danger: 'Опасная зона',
    deleteTitle: 'Удалить проект',
    deleteText:
      'Проект уедет в корзину вместе с досками и задачами. Восстановление — через поддержку.',
    delete: 'Удалить проект',
    deleteConfirmTitle: 'Удалить проект?',
    deleted: 'Проект удалён',
    unavailable: 'Проект недоступен',
    unavailableText: 'Его нет, он удалён или вы к нему не допущены.',
    toWorkspace: 'К пространству',
  },
  board: {
    createTitle: 'Новая доска',
    createText: 'Доска — набор задач со статусами. Начнёт с пяти статусов, их можно поменять.',
    name: 'Название',
    namePlaceholder: 'Основная',
    slug: 'Адрес',
    create: 'Создать доску',
    created: 'Доска создана',
    slugTaken: 'Этот адрес уже занят',
    empty: 'Досок пока нет',
    emptyText: 'Создайте первую — и в ней появятся статусы и задачи.',
    emptyTextMember: 'Доски создаёт администратор проекта.',
    open: 'Открыть',
    columns: 'Статусы',
    configure: 'Настроить доску',
    settingsTitle: 'Настройки доски',
    noTasks: 'Задач пока нет',
    tasksSoon: 'Задачи появятся в следующем шаге',
    unavailable: 'Доска недоступна',
    toSpace: 'К проекту',
    rename: 'Переименовать',
    delete: 'Удалить доску',
    deleteText: 'Доска уедет в корзину вместе с задачами.',
    deleted: 'Доска удалена',
    renamed: 'Доска переименована',
    views: {
      board: 'Доска',
      list: 'Список',
      table: 'Таблица',
      calendar: 'Календарь',
      timeline: 'Таймлайн',
      soon: 'Скоро',
      settings: 'Настройки вида',
      cardFields: 'Поля карточки',
      cardFieldsHint: 'Общие для всех на этой доске.',
      cardFieldsReadonly: 'Меняет владелец или администратор проекта.',
      fieldSaved: 'Вид сохранён',
      fields: {
        key: 'Ключ',
        parent: 'Родительская задача',
        priority: 'Приоритет',
        tags: 'Теги',
        subtasks: 'Прогресс подзадач',
        checklist: 'Прогресс чеклиста',
        comments: 'Комментарии',
        files: 'Вложения',
        due: 'Дедлайн',
        start: 'Дата начала',
        assignees: 'Исполнители',
        reviewer: 'Ревьюер',
      },
    },
    filter: {
      button: 'Фильтр',
      add: 'Добавить фильтр',
      mine: 'Только мои',
      mineHint: 'Где я исполнитель или ревьюер',
      assignee: 'Исполнитель',
      reviewer: 'Ревьюер',
      priority: 'Приоритет',
      due: 'Дедлайн',
      tag: 'Теги',
      sprint: 'Спринт',
      currentSprint: 'Текущий',
      currentSprintNamed: (name: string) => `Текущий — ${name}`,
      noCurrentSprint: 'Текущий — сейчас не идёт ни один',
      noSprint: 'Без спринта',
      any: 'любой',
      anyTag: 'любые',
      missing: 'нет в проекте',
      dues: {
        overdue: 'Просрочено',
        today: 'Сегодня',
        week: 'На этой неделе',
        none: 'Без срока',
      },
      remove: 'Убрать фильтр',
      reset: 'Сбросить',
      shown: (shown: number, total: number) => `Показано ${String(shown)} из ${String(total)}`,
      nothing: 'Под фильтр ничего не подходит',
      properties: 'Свойства',
      yes: 'да',
    },
    sort: {
      button: 'Сортировка',
      properties: 'Свойства',
      manual: 'Вручную',
      key: 'Ключ',
      title: 'Название',
      status: 'Статус',
      priority: 'Приоритет',
      due: 'Дедлайн',
      start: 'Дата начала',
      created: 'Создана',
      updated: 'Изменена',
      asc: 'По возрастанию',
      desc: 'По убыванию',
      hint: 'При сортировке порядок внутри статуса задаёт она, а не перетаскивание.',
    },
    search: 'Поиск по доске',
    searchPlaceholder: 'Ключ, название, описание…',
    group: {
      button: 'Группировка',
      status: 'По статусу',
      assignee: 'По исполнителю',
      priority: 'По приоритету',
      none: 'Без группировки',
      properties: 'По свойству',
    },
    reminders: {
      title: 'Напоминания о сроке',
      text: 'Исполнителям придёт уведомление за столько дней до дедлайна, если у задачи не задано своё.',
      saved: 'Умолчание напоминаний сохранено',
    },
    statuses: {
      title: 'Статусы',
      text: 'Название и цвет — любые. Флажок «завершает» говорит системе, что задачи с этим статусом сделаны: по нему работают фильтр незакрытого и статистика.',
      add: 'Добавить статус',
      newName: 'Новый статус',
      name: 'Название',
      doneFlag: 'Завершает',
      color: 'Цвет',
      remove: 'Удалить статус',
      lastOne: 'Последний статус удалить нельзя',
      saved: 'Статусы сохранены',
      dragHint: 'Перетащите, чтобы изменить порядок',
      done: 'Готово',
    },
  },
  sprints: {
    create: 'Новый спринт',
    createTitle: 'Новый спринт',
    editTitle: 'Спринт',
    name: 'Название',
    goal: 'Цель',
    goalPlaceholder: 'Что команда хочет успеть — одной-двумя фразами',
    start: 'Начало',
    end: 'Конец',
    save: 'Сохранить',
    created: 'Спринт создан',
    saved: 'Спринт сохранён',
    deleted: 'Спринт удалён',
    endBeforeStart: 'Конец не может быть раньше начала',
    states: { active: 'Идёт', planned: 'Запланирован', completed: 'Завершён' },
    groups: { active: 'Идёт', planned: 'Запланированы', completed: 'Завершённые' },
    empty: 'Спринтов пока нет',
    emptyText:
      'Спринт — отрезок в пару недель, за который команда берёт набор задач. Заведите первый и добавьте в него задачи — в свойствах задачи или фильтром на доске.',
    progress: (done: number, total: number) => `${String(done)} из ${String(total)}`,
    doneOf: (done: number, total: number) => `готово ${String(done)} из ${String(total)}`,
    daysLeft: (days: number) =>
      days === 0
        ? 'последний день'
        : days > 0
          ? `осталось ${String(days)} дн.`
          : `просрочен на ${String(-days)} дн.`,
    startsIn: (days: number) => (days <= 0 ? 'начинается сегодня' : `через ${String(days)} дн.`),
    complete: 'Завершить',
    completeTitle: (name: string) => `Завершить «${name}»?`,
    completeText: (open: number) =>
      open === 0
        ? 'Все задачи готовы — они останутся в спринте как его история.'
        : `Готовые задачи останутся в спринте. Незакрытые (${String(open)}) — куда их?`,
    moveTo: 'Перенести незакрытые',
    moveNowhere: 'Оставить без спринта',
    completed: (moved: number) =>
      moved === 0 ? 'Спринт завершён' : `Спринт завершён, перенесено задач: ${String(moved)}`,
    edit: 'Изменить',
    remove: 'Удалить',
    removeTitle: (name: string) => `Удалить «${name}»?`,
    removeText: 'Задачи останутся в проекте без спринта. История спринта и график пропадут.',
    burndown: 'Burndown',
    burndownHint: 'Сколько задач осталось по дням. Пунктир — идеальный темп.',
    burndownEmpty: 'График появится, когда спринт начнётся.',
    remaining: 'Осталось',
    ideal: 'Идеальный темп',
    openBoard: 'Открыть на доске',
  },
  task: {
    add: 'Добавить задачу',
    addPlaceholder: 'Название задачи',
    created: 'Задача создана',
    title: 'Название',
    titlePlaceholder: 'Без названия',
    description: 'Описание',
    status: 'Статус',
    priority: 'Приоритет',
    priorities: {
      none: 'Без приоритета',
      low: 'Низкий',
      normal: 'Обычный',
      high: 'Высокий',
      urgent: 'Срочно',
    },
    assignees: 'Исполнители',
    addAssignee: 'Добавить исполнителя',
    reviewer: 'Ревьюер',
    addReviewer: 'Назначить ревьюера',
    noReviewer: 'Не назначен',
    dueDate: 'Дедлайн',
    startDate: 'Дата начала',
    noDate: 'Не задан',
    remind: 'Напоминание',
    remindDefault: (text: string) => `Как у доски · ${text}`,
    remindOnDay: 'В день срока',
    remindBefore: (n: number) => `За ${pluralize(n, ['день', 'дня', 'дней'])}`,
    remindNoDue: 'Сработает, когда появится дедлайн',
    repeat: 'Повтор',
    sprint: 'Спринт',
    noSprint: 'Без спринта',
    repeatNone: 'Без повтора',
    repeatAfterClose: 'После закрытия',
    repeatAfterCloseIn: (n: number) =>
      `Через ${pluralize(n, ['день', 'дня', 'дней'])} после закрытия`,
    repeatEvery: {
      daily: (n: number) =>
        n === 1 ? 'Каждый день' : `Каждые ${pluralize(n, ['день', 'дня', 'дней'])}`,
      weekly: (n: number) =>
        n === 1 ? 'Каждую неделю' : `Каждые ${pluralize(n, ['неделю', 'недели', 'недель'])}`,
      monthly: (n: number) =>
        n === 1 ? 'Каждый месяц' : `Каждые ${pluralize(n, ['месяц', 'месяца', 'месяцев'])}`,
      yearly: (n: number) =>
        n === 1 ? 'Каждый год' : `Каждые ${pluralize(n, ['год', 'года', 'лет'])}`,
    },
    repeatOf: 'Повтор задачи',
    tags: 'Теги',
    addTag: 'Добавить тег',
    newTag: 'Создать тег',
    createdBy: 'Создал',
    createdAt: 'Создана',
    delete: 'Удалить задачу',
    deleteText: 'Задача уедет в корзину. Ключ останется занятым.',
    deleted: 'Задача удалена',
    copyKey: 'Скопировать ключ',
    copyLink: 'Скопировать ссылку',
    linkCopied: 'Ссылка скопирована',
    close: 'Закрыть',
    empty: 'Пусто',
    properties: 'Свойства',
    openAsWindow: 'Открыть окном',
    openAsPanel: 'Открыть панелью',
    openPage: 'Открыть на странице',
    toBoard: 'К доске',
    unavailable: 'Задача недоступна',
    unavailableText: 'Её нет, она удалена или вы не допущены к её проекту.',
    parent: 'Подзадача в',
    subtasks: 'Подзадачи',
    addSubtask: 'Добавить подзадачу',
    subtaskPlaceholder: 'Название подзадачи',
    checklist: 'Чеклист',
    addItem: 'Добавить пункт',
    itemPlaceholder: 'Что нужно сделать',
    itemText: 'Текст пункта',
    removeItem: 'Удалить пункт',
    saved: 'Сохранено',
    overdue: 'просрочено',
    today: 'сегодня',
    tomorrow: 'завтра',
    search: 'Поиск…',
    nothingFound: 'Ничего не найдено',
    done: 'Готово',
    formerMember: 'Не в команде — можно только снять',
  },
  myTasks: {
    title: 'Мои задачи',
    subtitle: 'Всё, где вы исполнитель или ревьюер — из всех проектов сразу.',
    empty: 'Пока ничего не назначено',
    emptyHint: 'Задачи появятся здесь, как только вас назначат исполнителем или ревьюером.',
    emptyDone: 'Ничего не найдено',
    roleAll: 'Все',
    roleAssignee: 'Я исполнитель',
    roleReviewer: 'Я ревьюер',
    reviewer: 'ревьюер',
    showDone: 'Показать завершённые',
    group: 'Группировка',
    groups: {
      due: 'По сроку',
      space: 'По проекту',
      priority: 'По приоритету',
    },
    due: {
      overdue: 'Просрочено',
      today: 'Сегодня',
      week: 'На неделе',
      later: 'Позже',
      none: 'Без срока',
    },
    noSpace: 'Без проекта',
    total: (n: number) => `${String(n)} задач`,
  },
  pages: {
    title: 'Страницы',
    spaceTitle: 'Страницы проекта',
    add: 'Новая страница',
    open: 'Открыть',
    addChild: 'Подстраница',
    untitled: 'Без названия',
    titlePlaceholder: 'Название страницы',
    placeholder: 'Начните писать… «/» — блоки',
    empty: 'Страниц пока нет',
    emptyHint: 'Регламенты, доступы, заметки — всё, что нужно команде под рукой.',
    children: 'Подстраницы',
    created: 'Страница создана',
    deleted: 'Страница удалена',
    delete: 'Удалить страницу',
    deleteTitle: (title: string) => `Удалить «${title}»?`,
    deleteText: 'Вместе со всеми вложенными страницами.',
    unavailable: 'Страница недоступна',
    unavailableText: 'Её нет, она удалена или вам не открыт доступ.',
    icon: 'Иконка',
    removeIcon: 'Убрать иконку',
    expand: 'Развернуть',
    collapse: 'Свернуть',
    move: 'Перетащить',
    moved: 'Страница перемещена',
    access: {
      button: 'Доступ',
      title: 'Кому открыта страница',
      all: 'Всем',
      allHint: 'Каждому, кто видит этот уровень',
      roles: 'По ролям',
      rolesHint: 'Только выбранным ролям',
      people: 'Людям',
      peopleHint: 'Только перечисленным участникам',
      pick: 'Добавить человека',
      saved: 'Доступ сохранён',
      managersNote: 'Владелец и администраторы видят все страницы.',
      hiddenNote: 'Вложенные страницы наследуют ограничение: кто не видит эту, не увидит и их.',
      summary: {
        all: 'всем',
        roles: 'по ролям',
        people: 'людям',
      },
    },
  },
  templates: {
    task: {
      button: 'Из шаблона',
      empty: 'Шаблонов пока нет',
      emptyHint: 'Откройте задачу и нажмите «Сделать шаблоном» — она станет заготовкой для новых.',
      create: (name: string) => `Создать «${name}»`,
      edit: 'Изменить шаблоны',
      makeTemplate: 'Сделать шаблоном',
      unmakeTemplate: 'Сделать обычной задачей',
      made: 'Теперь это шаблон',
      unmade: 'Снова обычная задача',
      banner: 'Это шаблон',
      bannerText:
        'На доске не показывается. Новые задачи из него — кнопкой «Из шаблона» над доской.',
      created: 'Задача создана из шаблона',
    },
    space: {
      title: 'Шаблон проекта',
      text: 'Проект-образец: новые проекты можно создавать по нему — со свойствами, тегами и досками со статусами. Задачи не копируются.',
      toggle: 'Использовать как шаблон',
      made: 'Проект стал шаблоном',
      unmade: 'Проект больше не шаблон',
      pick: 'Шаблон',
      none: 'Без шаблона',
      badge: 'Шаблон',
      hint: 'Свойства, теги и доски со статусами будут скопированы из шаблона.',
    },
  },
  properties: {
    add: 'Добавить свойство',
    namePlaceholder: 'Название свойства',
    type: 'Тип',
    types: {
      text: 'Текст',
      number: 'Число',
      select: 'Выбор',
      multi_select: 'Несколько из списка',
      person: 'Человек',
      date: 'Дата',
      link: 'Ссылка',
      checkbox: 'Флажок',
    },
    created: 'Свойство добавлено',
    rename: 'Переименовать',
    renamed: 'Свойство переименовано',
    delete: 'Удалить свойство',
    deleteTitle: (name: string) => `Удалить свойство «${name}»?`,
    deleteText:
      'Свойство пропадёт у всех задач организации. Заполненные значения останутся в базе, но показываться не будут.',
    deleted: 'Свойство удалено',
    newOption: 'Создать вариант',
    searchOption: 'Найти вариант…',
    invalidNumber: 'Это не число',
    spaceTitle: 'Свойства проекта',
    progress: 'Прогресс',
    progressText: (done: number, total: number) => `${String(done)} из ${String(total)}`,
    noTasks: 'Задач пока нет',
    openLink: 'Открыть ссылку',
    linkPlaceholder: 'https://',
  },
  table: {
    selectAll: 'Выбрать все',
    selectRow: 'Выбрать строку',
    selected: (n: number) => `Выбрано: ${pluralize(n, ['задача', 'задачи', 'задач'])}`,
    addAssignee: 'Назначить',
    delete: 'Удалить',
    deleteTitle: (n: number) => `Удалить ${pluralize(n, ['задачу', 'задачи', 'задач'])}?`,
    deleteText: 'Задачи уедут в корзину вместе с подзадачами. Ключи останутся занятыми.',
    clearSelection: 'Снять выбор',
    columnsTitle: 'Столбцы таблицы',
    columnsHint: 'Общие для всех на этой доске.',
    columns: {
      key: 'Ключ',
      title: 'Название',
      status: 'Статус',
      priority: 'Приоритет',
      assignees: 'Исполнители',
      reviewer: 'Ревьюер',
      due: 'Дедлайн',
      start: 'Дата начала',
      tags: 'Теги',
      subtasks: 'Подзадачи',
      checklist: 'Чеклист',
      comments: 'Комментарии',
      files: 'Вложения',
      created: 'Создана',
      updated: 'Изменена',
    },
  },
  calendar: {
    title: 'Календарь дедлайнов',
    today: 'Сегодня',
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    noDue: 'Без дедлайна',
    noDueHint: 'Перетащите задачу на нужный день',
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  },
  timeline: {
    title: 'Таймлайн задач',
    today: 'Сегодня',
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    task: 'Задача',
    noDates: 'Без дат',
    noDatesHint: 'Добавьте даты в панели задачи, чтобы она появилась на шкале',
    changeStart: 'Изменить дату начала',
    changeEnd: 'Изменить дедлайн',
    empty: 'На этом таймлайне пока нет задач',
  },
  savedViews: {
    create: 'Сохранить текущий вид',
    createTitle: 'Новый сохранённый вид',
    createText: 'Тип отображения, фильтры, группировка и сортировка сохранятся для всей команды.',
    name: 'Название',
    namePlaceholder: 'Например, Мои задачи',
    nameRequired: 'Введите название вида',
    created: 'Вид сохранён',
    actions: 'Действия с сохранённым видом',
    rename: 'Переименовать',
    renameTitle: 'Переименовать вид',
    renamed: 'Вид переименован',
    delete: 'Удалить вид',
    deleteTitle: (name: string) => `Удалить вид «${name}»?`,
    deleteText: 'Задачи и сама доска останутся на месте. Удалится только сохранённая конфигурация.',
    deleted: 'Вид удалён',
  },
  list: {
    noValue: 'Без значения',
    yes: 'Да',
    noAssignee: 'Без исполнителя',
    empty: 'Пусто',
    expand: 'Показать подзадачи',
    collapse: 'Скрыть подзадачи',
    editTitle: 'Нажмите, чтобы изменить название',
  },
  members: {
    title: 'Участники',
    subtitle: 'Кто в пространстве и с какими правами.',
    invite: 'Пригласить',
    inviteTitle: 'Пригласить в пространство',
    inviteText: 'Ссылка покажется один раз — отправьте её человеку сами.',
    email: 'Адрес почты',
    roleLabel: 'Роль',
    createLink: 'Создать ссылку',
    linkReady: 'Ссылка готова',
    linkReadyText: 'Отправьте её. Она действует 7 дней и открывается один раз.',
    copyLink: 'Скопировать ссылку',
    done: 'Готово',
    pending: 'Ожидают принятия',
    expires: 'до',
    revoke: 'Отозвать',
    revoked: 'Приглашение отозвано',
    spaces: 'Проекты',
    spacesOf: (name: string) => `Проекты: ${name}`,
    noSpaces: 'В пространстве пока нет проектов.',
    seesAll: {
      owner: 'Владелец видит все проекты — добавлять не нужно.',
      admin: 'Администратор видит все проекты — добавлять не нужно.',
      member: '',
      guest: '',
    },
    remove: 'Исключить',
    removeTitle: 'Исключить из пространства?',
    removeText: 'Человек потеряет доступ ко всем проектам и задачам пространства.',
    removed: 'Участник исключён',
    roleChanged: 'Роль изменена',
    you: 'вы',
    invitedStatus: 'ещё не вошёл',
    blockedStatus: 'заблокирован',
    count: ['участник', 'участника', 'участников'],
  },
  invite: {
    title: 'Приглашение',
    intro: 'Вас приглашают в пространство',
    as: 'как',
    forEmail: 'Приглашение выписано на',
    name: 'Ваше имя',
    password: 'Придумайте пароль',
    accept: 'Принять и войти',
    acceptExisting: 'Принять приглашение',
    signInFirst: 'У этого адреса уже есть учётная запись. Войдите, чтобы принять приглашение.',
    signIn: 'Войти',
    wrongAccount: 'Вы вошли под другим адресом. Выйдите и войдите под',
    invalid: 'Ссылка недействительна',
    invalidText:
      'Приглашение отозвано, уже принято или срок его действия истёк. Попросите новую ссылку.',
    accepted: 'Добро пожаловать',
  },
  profile: {
    title: 'Профиль',
    subtitle: 'Имя, которое видят коллеги, и пароль для входа.',
    nameSection: 'Имя',
    name: 'Имя',
    nameHint: 'Так вас будут видеть в задачах и комментариях.',
    nameRequired: 'Имя не может быть пустым',
    email: 'Адрес почты',
    emailHint: 'Это ваш логин. Смена адреса появится вместе с почтой.',
    notificationsSection: 'Уведомления',
    notificationsText:
      'Что присылать и куда. Выключенное в колокольчике не создаётся вовсе и никуда не уходит. Почта появится здесь же.',
    notificationEvent: 'Событие',
    notificationKinds: {
      assigned: 'Меня назначили исполнителем',
      reviewer: 'Меня назначили ревьюером',
      mentioned: 'Меня упомянули',
      replied: 'Ответили на мой комментарий',
      status: 'Мою задачу перенесли в другой статус',
      due: 'Подходит срок задачи',
      repeated: 'Создан повтор моей задачи',
    },
    notificationChannels: {
      app: 'Колокольчик',
      telegram: 'Telegram',
      push: 'Push',
    },
    notificationSaved: 'Настройка сохранена',
    telegram: {
      title: 'Telegram',
      text: 'Уведомления в личку от бота, а любое сообщение боту становится задачей. Что присылать — в таблице выше, столбец «Telegram».',
      disabled: 'В этом окружении бот не настроен.',
      linked: (name: string) => `Привязан: ${name}`,
      linkedNoName: 'Привязан',
      link: 'Привязать Telegram',
      unlink: 'Отвязать',
      unlinked: 'Telegram отвязан',
      open: 'Открыть бота',
      codeHint: (bot: string) =>
        `Откройте бота и нажмите «Start» — или напишите @${bot} команду с кодом. Код действует 15 минут.`,
      waiting: 'Ждём подтверждения от бота…',
      done: 'Готово — Telegram привязан',
      inbox: 'Входящая доска',
      inboxPlaceholder: 'Бот спросит при первом сообщении',
      inboxHint:
        'Сообщение боту становится задачей в этой доске: первая строка — название, остальное — описание. Сменить можно и в самом боте командой /board.',
      inboxSaved: 'Входящая доска сохранена',
      inboxCleared: 'Бот спросит доску при следующем сообщении',
      inboxNoBoards: 'Нет доски, куда можно складывать задачи: попросите добавить вас в проект.',
    },
    device: {
      title: 'Это устройство',
      text: 'Установка и push-уведомления включаются на каждом устройстве отдельно. Что присылать — в таблице выше, столбец «Push».',
      appTitle: 'Приложение',
      install: 'Установить',
      installText: 'Своя иконка на домашнем экране и окно без адресной строки.',
      installed: 'Приложение установлено',
      installedText: 'Открыто как установленное приложение.',
      installIOS:
        'В Safari: «Поделиться» → «На экран „Домой“». Без этого push на iPhone не работает.',
      installManual:
        'Установка — из меню браузера: «Установить приложение» или «Добавить на главный экран».',
      pushTitle: 'Push-уведомления',
      pushOffText: 'Уведомления на это устройство, даже когда вкладка закрыта.',
      pushOnText: 'Включены на этом устройстве.',
      pushTurnOn: 'Включить',
      pushTurnOff: 'Выключить',
      pushOn: 'Push включён на этом устройстве',
      pushOff: 'Push выключен на этом устройстве',
      pushDenied: 'Уведомления запрещены в браузере',
      pushBlocked:
        'Уведомления запрещены для этого сайта — разрешите их в настройках браузера и обновите страницу.',
      pushNeedsInstall:
        'На iPhone push работает только у установленного приложения — установите его сначала.',
      pushUnsupported: 'Этот браузер не поддерживает push-уведомления.',
      pushNoWorker:
        'Не удалось запустить фоновый обработчик — обновите страницу и попробуйте ещё раз.',
      pushDisabled: 'В этом окружении push не настроен.',
    },
    passwordSection: 'Пароль',
    passwordText:
      'После смены все остальные устройства выйдут из системы. Это устройство останется.',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    newPasswordAgain: 'Новый пароль ещё раз',
    passwordsDiffer: 'Пароли не совпадают',
    changePassword: 'Сменить пароль',
    passwordChanged: 'Пароль сменён',
    passwordChangedText: 'Остальные устройства вышли из системы.',
    superAdmin: 'Владелец платформы',
  },
  errorBoundary: {
    title: 'Что-то сломалось',
    text: 'Страницу не удалось отобразить. Обычно помогает перезагрузка — если нет, сообщите об ошибке с текстом ниже.',
  },
  editor: {
    placeholder: 'Начните писать или введите «/» для блоков…',
    saved: 'Сохранено',
    saving: 'Сохранение…',
    slashHint: 'Блоки',
    nothing: 'Ничего не найдено',
    blocks: {
      text: 'Текст',
      textHint: 'Обычный абзац',
      h1: 'Заголовок 1',
      h2: 'Заголовок 2',
      h3: 'Заголовок 3',
      headingHint: 'Раздел описания',
      bullet: 'Список',
      bulletHint: 'Маркированный',
      ordered: 'Нумерованный список',
      orderedHint: '1, 2, 3',
      todo: 'Чеклист',
      todoHint: 'Пункты с галочками',
      quote: 'Цитата',
      quoteHint: 'Выделенный фрагмент',
      code: 'Код',
      codeHint: 'Блок с подсветкой',
      divider: 'Разделитель',
      dividerHint: 'Горизонтальная линия',
      image: 'Картинка',
      imageHint: 'По ссылке; загрузка файлов — в 3.2',
      table: 'Таблица',
      tableHint: 'Выбрать размер сеткой',
      access: 'Доступы',
      accessHint: 'Сайты, логины и пароли под замком',
    },
    access: {
      title: 'Доступы',
      site: 'Сайт',
      url: 'Адрес',
      login: 'Логин',
      password: 'Пароль',
      note: 'Заметка',
      addRow: 'Добавить строку',
      removeRow: 'Удалить строку',
      addColumn: 'Добавить столбец',
      removeColumn: 'Удалить столбец',
      columnName: 'Название столбца',
      kinds: {
        text: 'Текст',
        url: 'Ссылка',
        secret: 'Секрет',
      },
      secretLocked: 'Секрет нельзя превратить обратно в текст — только удалить столбец',
      set: 'Задать пароль',
      change: 'Сменить пароль',
      reveal: 'Показать',
      hide: 'Скрыть',
      copy: 'Скопировать',
      copied: 'Пароль скопирован',
      empty: 'не задан',
      saving: 'Сохраняется…',
      log: 'Журнал',
      logTitle: 'Кто смотрел пароли',
      logEmpty: 'Пока никто не смотрел',
      actions: {
        reveal: 'показал',
        copy: 'скопировал',
        set: 'задал',
      },
      unavailable: 'Доступы недоступны',
      unavailableText:
        'В этом окружении не настроен ключ шифрования — попросите администратора сервера.',
      onlyPages: 'Блок «Доступы» работает только на страницах.',
      openLink: 'Открыть',
    },
    context: {
      turnInto: 'Превратить в',
      duplicate: 'Дублировать',
      deleteBlock: 'Удалить блок',
      format: 'Формат',
      table: 'Таблица',
    },
    table: {
      size: (rows: number, cols: number) => `${String(cols)} × ${String(rows)}`,
      pick: 'Размер таблицы',
      rowAbove: 'Строка выше',
      rowBelow: 'Строка ниже',
      colLeft: 'Столбец слева',
      colRight: 'Столбец справа',
      deleteRow: 'Удалить строку',
      deleteCol: 'Удалить столбец',
      header: 'Строка-заголовок',
      deleteTable: 'Удалить таблицу',
    },
    format: {
      bold: 'Жирный',
      italic: 'Курсив',
      strike: 'Зачёркнутый',
      code: 'Код',
      link: 'Ссылка',
      unlink: 'Убрать ссылку',
      openLink: 'Открыть в новой вкладке',
      editLink: 'Изменить адрес',
    },
    linkPrompt: 'Адрес ссылки',
    imagePrompt: 'Адрес картинки',
    apply: 'Готово',
    mentionHint: 'Кого упомянуть',
  },
  inbox: {
    title: 'Входящие',
    subtitle: 'Что касается лично вас: назначения, упоминания, ответы.',
    unread: 'Непрочитанные',
    all: 'Все',
    readAll: 'Прочитать всё',
    empty: 'Пока ничего — когда вас назначат, упомянут или ответят, это появится здесь.',
    emptyUnread: 'Непрочитанных нет.',
    open: 'Все входящие',
    kinds: {
      assigned: 'назначил вас исполнителем',
      reviewer: 'назначил вас ревьюером',
      mentionedTask: 'упомянул вас в описании',
      mentionedComment: 'упомянул вас в комментарии',
      replied: 'ответил на ваш комментарий',
      status: (from: string, to: string) => `перенёс задачу из «${from}» в «${to}»`,
      dueToday: 'срок задачи — сегодня',
      dueTomorrow: 'срок задачи — завтра',
      dueIn: (n: number) => `срок задачи через ${pluralize(n, ['день', 'дня', 'дней'])}`,
      dueOverdue: (n: number) =>
        `срок задачи прошёл ${pluralize(n, ['день', 'дня', 'дней'])} назад`,
      dueOn: (date: string) => `срок задачи — ${date}`,
      repeated: (key: string) => `создал повтор задачи ${key}`,
      system: 'Flow Do',
    },
  },
  history: {
    title: 'История',
    show: 'Показать',
    hide: 'Скрыть',
    empty: 'Пока пусто.',
    none: '—',
    created: 'создал задачу',
    title_: (from: string, to: string) => `переименовал: «${from}» → «${to}»`,
    description: 'изменил описание',
    priority: (from: string, to: string) => `приоритет: ${from} → ${to}`,
    startDate: (from: string, to: string) => `дата начала: ${from} → ${to}`,
    dueDate: (from: string, to: string) => `дедлайн: ${from} → ${to}`,
    reviewer: (from: string, to: string) => `ревьюер: ${from} → ${to}`,
    assigneeAdded: (name: string) => `добавил исполнителя ${name}`,
    assigneeRemoved: (name: string) => `убрал исполнителя ${name}`,
    tagAdded: (name: string) => `добавил тег ${name}`,
    tagRemoved: (name: string) => `убрал тег ${name}`,
    status: (from: string, to: string) => `перенёс из «${from}» в «${to}»`,
    parentSet: (key: string) => `сделал подзадачей ${key}`,
    parentUnset: 'сделал самостоятельной задачей',
    deleted: 'удалил задачу',
  },
  files: {
    title: 'Вложения',
    add: 'Прикрепить файл',
    drop: 'Отпустите, чтобы загрузить',
    uploading: 'Загрузка…',
    download: 'Скачать',
    remove: 'Удалить файл',
    removeTitle: 'Удалить файл?',
    removeText: 'Файл исчезнет из задачи и из комментария, к которому был приложен.',
    tooLarge: 'Файл больше 50 МБ',
    failed: 'Не удалось загрузить файл',
    empty: 'Перетащите файлы сюда или нажмите «Прикрепить».',
    open: 'Открыть в новой вкладке',
    close: 'Закрыть',
    prev: 'Предыдущий',
    next: 'Следующий',
    noPreview: 'Предпросмотра нет — такие файлы браузер не показывает.',
    tooBig: 'Файл слишком большой для показа — скачайте его.',
    uploadedBy: (name: string) => `Загрузил ${name}`,
    zoom: 'Увеличить',
  },
  presence: {
    online: (name: string) => `${name} в сети`,
    viewing: (name: string) => `${name} смотрит задачу`,
    typing: (names: string[]) =>
      names.length === 1 ? `${names[0] ?? ''} печатает…` : `${names.join(', ')} печатают…`,
    offline: 'Нет связи с сервером — переподключаемся…',
  },
  date: {
    today: 'Сегодня',
    tomorrow: 'Завтра',
    nextWeek: 'Через неделю',
    clear: 'Убрать дату',
    prevMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
  },
  time: {
    justNow: 'только что',
    minutesAgo: (n: number) => `${String(n)} мин назад`,
    hoursAgo: (n: number) => `${String(n)} ч назад`,
    yesterday: 'вчера',
  },
  comments: {
    title: 'Комментарии',
    placeholder: 'Написать комментарий… @ — упомянуть',
    replyPlaceholder: 'Ответить…',
    send: 'Отправить',
    reply: 'Ответить',
    edit: 'Изменить',
    delete: 'Удалить',
    deleteTitle: 'Удалить комментарий?',
    deleteText: 'Если на него отвечали, вместо текста останется пометка «удалён».',
    deleted: 'Комментарий удалён',
    edited: 'изменён',
    react: 'Реакция',
    allEmoji: 'Все эмодзи',
    save: 'Сохранить',
    cancel: 'Отмена',
    empty: 'Пока никто ничего не написал.',
    mentionHint: 'Кого упомянуть',
    someone: 'участник',
  },
  notFound: {
    title: 'Такой страницы нет',
    text: 'Возможно, адрес набран с опечаткой или раздел ещё не сделан.',
    home: 'Вернуться на главную',
  },
  status: {
    title: 'Состояние',
    subtitle: 'Проверка связи фронтенда с бэкендом через сгенерированный из спеки клиент.',
    frontend: 'Фронтенд',
    backend: 'Бэкенд',
    database: 'База данных',
    environment: 'Окружение',
    apiBase: 'Адрес API',
    ok: 'Работает',
    checking: 'Проверяем…',
    unavailable: 'Недоступен',
    requestId: 'Идентификатор запроса',
  },
} as const;
